/**
 * The Alpine half of <x-insights.toast> (D67, D71): the Insights page's one toast, at the bottom centre,
 * which any part of the page shows with a window event:
 *
 *   $dispatch('insights-toast', { message: '…', undo: { event: 'site-topic-undo', detail: { … } } })
 *   $this->dispatch('insights-toast', message: '…', undo: ['event' => '…', 'detail' => […]])  // Livewire
 *
 * `waitFor` is optional too: the name of a window event the message waits for before it shows, at most
 * WAIT_FOR_MS. Copy prompt's toast waits for the drawer to close (DraftArticle::COPY_CLOSED_EVENT, P83), so it
 * never covers the drawer's footer line, which says the same while the drawer is open.
 *
 * `undo` is optional. Its Undo button sends `undo.event` with `undo.detail` and hides the toast; the
 * screen that showed it listens for that event, and moves focus to what it put back, since the toast
 * that held focus is gone. The toast is only the announcement: the Undo that lasts sits on the slim row
 * or in place (D71; WCAG 2.2.1). It enters over the `show` token, hides after 4.2 seconds, and waits
 * while it is hovered or holds focus.
 */
const HIDE_AFTER_MS = 4200;

/** The longest a message waits for its `waitFor` event: past it, the message shows anyway, never lost. */
export const WAIT_FOR_MS = 4000;

export default function InsightsToast() {
    return {
        it_open: false,
        it_message: '',
        it_undo: null,
        it_timer: null,
        it_left: HIDE_AFTER_MS,
        it_since: 0,

        it_waiting: null,

        it_show(detail) {
            this.it_stopWaiting();

            if (typeof detail?.waitFor === "string" && detail.waitFor !== "") {
                const shown = { ...detail, waitFor: null };
                const show = () => {
                    this.it_stopWaiting();
                    this.it_show(shown);
                };
                const timer = setTimeout(show, WAIT_FOR_MS);

                window.addEventListener(detail.waitFor, show, { once: true });
                this.it_waiting = () => {
                    clearTimeout(timer);
                    window.removeEventListener(detail.waitFor, show);
                };

                return;
            }

            const message = detail?.message ?? '';

            const undo = detail?.undo?.event ? detail.undo : null;

            this.it_left = HIDE_AFTER_MS;
            // Emptied first, so the same message twice in a row still changes the status region, and
            // is announced again. The Undo comes back with the message, in one change: set before it,
            // the region read "Undo" alone, and a screen reader said it ahead of every message.
            this.it_message = '';
            this.it_undo = null;
            this.$nextTick(() => {
                this.it_undo = undo;
                this.it_message = message;
                this.it_open = message !== '';
                this.it_resume();
            });
        },

        // A message still waiting for its event is dropped when another one comes: the newer one says what is true now.
        it_stopWaiting() {
            this.it_waiting?.();
            this.it_waiting = null;
        },

        it_pause() {
            if (this.it_timer === null) {
                return;
            }

            clearTimeout(this.it_timer);
            this.it_timer = null;
            this.it_left = Math.max(0, this.it_left - (Date.now() - this.it_since));
        },

        it_resume() {
            clearTimeout(this.it_timer);
            this.it_since = Date.now();
            this.it_timer = this.it_open ? setTimeout(() => this.it_hide(), this.it_left) : null;
        },

        it_hide() {
            clearTimeout(this.it_timer);
            this.it_timer = null;
            this.it_open = false;
        },

        it_runUndo() {
            const undo = this.it_undo;

            this.it_hide();
            this.$dispatch(undo.event, undo.detail ?? {});
        },
    };
}
